import React, { useState, useEffect } from 'react';
import { Navigation } from './components/Navigation';
import { HeroSection } from './components/sections/HeroSection';
import { AboutSection } from './components/sections/AboutSection';
import { ExperienceSection } from './components/sections/ExperienceSection';
import { SkillsSection } from './components/sections/SkillsSection';
import { ProjectsSection } from './components/sections/ProjectsSection';
import { EducationSection } from './components/sections/EducationSection';
import Hackathons from './components/Hackathons';
import { ContactSection } from './components/sections/ContactSection';
import { LanguageProvider } from './contexts/LanguageContext';

// --- Color Palette Reference ---
// Dark BG: #0d0d0d
// Primary Red: #d92323
// Secondary Red: #732424
// Grey Text: #7b7b7b
// White Text: #ffffff

function App() {
    const [activeSection, setActiveSection] = useState('home');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // State for timeline expansion
    const [expandedExperience, setExpandedExperience] = useState(0); // First item open by default

    // Scroll handling
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Active section detection based on scroll position
    useEffect(() => {
        const sections = ['home', 'about', 'experience', 'skills', 'projects', 'education', 'hackathons', 'contact'];

        const observer = new IntersectionObserver(
            (entries) => {
                // Find the entry with the highest intersection ratio
                let maxRatio = 0;
                let activeEntry = null;

                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
                        maxRatio = entry.intersectionRatio;
                        activeEntry = entry;
                    }
                });

                if (activeEntry) {
                    setActiveSection(activeEntry.target.id);
                }
            },
            {
                rootMargin: '-10% 0px -60% 0px',
                threshold: [0.1, 0.2, 0.3, 0.4, 0.5]
            }
        );

        // Wait for next tick to ensure DOM is ready
        setTimeout(() => {
            const sectionElements = sections.map(id => document.getElementById(id)).filter(Boolean);
            sectionElements.forEach((element) => {
                if (element) observer.observe(element);
            });
        }, 100);

        return () => {
            observer.disconnect();
        };
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 80;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            // Remove manual setActiveSection - let Intersection Observer handle it
            setIsMenuOpen(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0d0d0d] text-white font-sans selection:bg-[#d92323] selection:text-white overflow-x-hidden">

            <Navigation
                activeSection={activeSection}
                scrolled={scrolled}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
                scrollToSection={scrollToSection}
            />

            <HeroSection scrollToSection={scrollToSection} />

            <AboutSection />

            <ExperienceSection
                expandedExperience={expandedExperience}
                setExpandedExperience={setExpandedExperience}
            />

            <SkillsSection />

            <ProjectsSection />

            <EducationSection />

            <Hackathons />

            <ContactSection />
        </div>
    );
}

// Wrap the entire app with LanguageProvider
const AppWithLanguage = () => {
    return (
        <LanguageProvider>
            <App />
        </LanguageProvider>
    );
};

export default AppWithLanguage;